import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, AlertTriangle, Check } from 'lucide-react';
import { useContentStore } from '../../store/contentStore';
import type { Category } from '../../types';

const GRADIENT_PRESETS = [
  { label: 'Violet',  color: 'from-violet-500 to-purple-600',  bg: 'bg-violet-50',  text: 'text-violet-700' },
  { label: 'Red',     color: 'from-red-400 to-rose-600',       bg: 'bg-red-50',     text: 'text-red-700' },
  { label: 'Blue',    color: 'from-blue-400 to-sky-600',       bg: 'bg-blue-50',    text: 'text-blue-700' },
  { label: 'Green',   color: 'from-green-400 to-emerald-600',  bg: 'bg-green-50',   text: 'text-green-700' },
  { label: 'Teal',    color: 'from-cyan-400 to-teal-600',      bg: 'bg-cyan-50',    text: 'text-cyan-700' },
  { label: 'Orange',  color: 'from-orange-400 to-amber-600',   bg: 'bg-orange-50',  text: 'text-orange-700' },
  { label: 'Pink',    color: 'from-pink-400 to-rose-500',      bg: 'bg-pink-50',    text: 'text-pink-700' },
  { label: 'Yellow',  color: 'from-yellow-400 to-amber-500',   bg: 'bg-yellow-50',  text: 'text-yellow-700' },
  { label: 'Lime',    color: 'from-lime-400 to-green-500',     bg: 'bg-lime-50',    text: 'text-lime-700' },
  { label: 'Indigo',  color: 'from-indigo-400 to-violet-500',  bg: 'bg-indigo-50',  text: 'text-indigo-700' },
];

interface FormState {
  name: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  icon: '',
  color: GRADIENT_PRESETS[0].color,
  bgColor: GRADIENT_PRESETS[0].bg,
  textColor: GRADIENT_PRESETS[0].text,
};

function formFromCategory(cat: Category): FormState {
  return {
    name: cat.name,
    description: cat.description,
    icon: cat.icon,
    color: cat.color,
    bgColor: cat.bgColor,
    textColor: cat.textColor,
  };
}

export default function CategoriesPanel() {
  const { categories, scenarios, addCategory, updateCategory, deleteCategory } =
    useContentStore();

  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError('');
    setModal('add');
  };

  const openEdit = (cat: Category) => {
    setEditTarget(cat);
    setForm(formFromCategory(cat));
    setError('');
    setModal('edit');
  };

  const handleSave = () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.icon.trim()) { setError('Icon is required.'); return; }
    if (!form.description.trim()) { setError('Description is required.'); return; }

    if (modal === 'add') {
      addCategory({
        name: form.name.trim(),
        description: form.description.trim(),
        icon: form.icon.trim(),
        color: form.color,
        bgColor: form.bgColor,
        textColor: form.textColor,
      });
    } else if (modal === 'edit' && editTarget) {
      updateCategory(editTarget.id, {
        name: form.name.trim(),
        description: form.description.trim(),
        icon: form.icon.trim(),
        color: form.color,
        bgColor: form.bgColor,
        textColor: form.textColor,
      });
    }
    setModal(null);
  };

  const handleDelete = (id: string) => {
    deleteCategory(id);
    setDeleteConfirm(null);
  };

  const setPreset = (preset: (typeof GRADIENT_PRESETS)[0]) => {
    setForm((f) => ({ ...f, color: preset.color, bgColor: preset.bg, textColor: preset.text }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {categories.length} categories
        </p>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={openAdd}
          className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-sm hover:bg-primary-700 transition-colors"
        >
          <Plus size={15} /> Add Category
        </motion.button>
      </div>

      <div className="space-y-2">
        {categories.map((cat) => {
          const scenarioCount = scenarios.filter((s) => s.categoryId === cat.id).length;
          return (
            <div
              key={cat.id}
              className="bg-white rounded-2xl p-4 shadow-card flex items-center gap-3"
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center text-xl flex-shrink-0`}>
                {cat.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-gray-800 text-sm leading-tight">{cat.name}</p>
                <p className="text-gray-400 text-xs mt-0.5 truncate">{cat.description}</p>
                <p className="text-xs text-primary-500 font-semibold mt-0.5">{scenarioCount} scenarios</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => openEdit(cat)}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-primary-100 text-gray-500 hover:text-primary-600 transition-colors"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => setDeleteConfirm(cat.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => setDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-x-4 top-1/3 z-50 bg-white rounded-3xl p-6 shadow-2xl max-w-sm mx-auto"
            >
              <AlertTriangle size={32} className="text-red-400 mx-auto mb-3" />
              <h3 className="font-display font-bold text-gray-800 text-center mb-2">Delete Category?</h3>
              <p className="text-gray-500 text-sm text-center mb-5">
                The category will be removed. Scenarios linked to it will remain in the store but won't appear in the library.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-2xl font-bold text-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 bg-red-500 text-white py-3 rounded-2xl font-bold text-sm hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {modal && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
              onClick={() => setModal(null)}
            />
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 280, damping: 28 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl px-4 pt-4 pb-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-lg font-extrabold text-gray-800">
                  {modal === 'add' ? 'Add Category' : 'Edit Category'}
                </h2>
                <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Icon + Name row */}
                <div className="flex gap-3">
                  <div className="w-20">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Icon</label>
                    <input
                      type="text"
                      value={form.icon}
                      onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                      placeholder="🎯"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-center text-xl outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Category name"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Description *</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Describe what this category covers"
                    rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                  />
                </div>

                {/* Colour preset */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Colour</label>
                  <div className="flex flex-wrap gap-2">
                    {GRADIENT_PRESETS.map((p) => (
                      <button
                        key={p.color}
                        onClick={() => setPreset(p)}
                        className={`w-8 h-8 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center transition-all ${
                          form.color === p.color ? 'ring-2 ring-offset-2 ring-gray-800 scale-110' : ''
                        }`}
                      >
                        {form.color === p.color && <Check size={14} className="text-white" />}
                      </button>
                    ))}
                  </div>

                  {/* Preview */}
                  <div className={`mt-3 flex items-center gap-3 bg-gradient-to-br ${form.color} rounded-2xl p-3`}>
                    <span className="text-2xl">{form.icon || '❓'}</span>
                    <div>
                      <p className="font-bold text-white text-sm">{form.name || 'Category Name'}</p>
                      <p className="text-white/70 text-xs">{form.description || 'Description'}</p>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSave}
                  className="w-full bg-primary-600 text-white py-3.5 rounded-2xl font-bold text-sm shadow-md hover:bg-primary-700 transition-colors"
                >
                  {modal === 'add' ? 'Add Category' : 'Save Changes'}
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
